import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Empty, Spin } from 'antd';
import moment from 'moment';
import Chart from 'chart.js/auto';
import { useTranslation } from 'react-i18next';
import theme from '../../../theme';
import { adsKeyToI18nKey, formatUnit } from '../../../utils/adsUtils';

function buildChartData(positions, adsKey, timelineTimestamps) {
  const empty = {
    labels: [], values: [], timestamps: [], hasAnyValue: false,
  };
  if (!positions || !positions.length) {
    return empty;
  }

  const valueByTs = new Map();
  positions.forEach((p) => {
    const ts = p && p.positionTimestamp;
    if (!ts) return;
    const val = p?.ads?.[adsKey];
    valueByTs.set(ts, (typeof val === 'number') ? val : null);
  });

  const baseTimestamps = Array.isArray(timelineTimestamps) && timelineTimestamps.length
    ? timelineTimestamps
    : positions
      .map((p) => p && p.positionTimestamp)
      .filter((x) => typeof x === 'number')
      .sort((a, b) => a - b);

  const labels = baseTimestamps.map((ts) => moment.unix(ts).format('DD.MM HH:mm'));
  const values = baseTimestamps.map((ts) => (valueByTs.has(ts) ? valueByTs.get(ts) : null));
  const hasAnyValue = values.some((v) => typeof v === 'number');

  return {
    labels, values, timestamps: baseTimestamps, hasAnyValue,
  };
}

const chartOptions = (label, unit, timestamps, adsKey) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        title: (items) => {
          const idx = items && items.length ? items[0].dataIndex : null;
          if (idx == null || !timestamps || !timestamps[idx]) return '';
          return moment.unix(timestamps[idx]).format('YYYY-MM-DD HH:mm');
        },
        label: (ctx) => {
          const v = ctx.parsed.y;
          if (v == null) return '';
          return `${label}: ${v}${unit}`;
        },
      },
    },
  },
  elements: {
    point: { radius: 0, hitRadius: 6, hoverRadius: 3 },
    line: { borderWidth: 1 },
  },
  layout: {
    padding: {
      left: 0, right: 0, top: 4, bottom: 0,
    },
  },
  scales: {
    x: {
      ticks: {
        maxRotation: 0,
        autoSkip: true,
        autoSkipPadding: 8,
        maxTicksLimit: 3,
        callback(value, index) {
          if (this && typeof this.getLabelForValue === 'function') {
            return this.getLabelForValue(value);
          }
          return (timestamps && timestamps[index])
            ? moment.unix(timestamps[index]).format('DD.MM HH:mm')
            : '';
        },
        font: { size: 10 },
      },
      grid: { display: false },
    },
    y: {
      beginAtZero: typeof adsKey === 'string' && adsKey.endsWith('Level'),
      min: (typeof adsKey === 'string' && adsKey.endsWith('Level')) ? 0 : undefined,
      max: (typeof adsKey === 'string' && adsKey.endsWith('Level')) ? 100 : undefined,
      grid: { color: 'rgba(0,0,0,0.05)' },
      ticks: {
        callback: (value) => `${value}${unit}`,
      },
    },
  },
});

export default function AdsTimeseriesChart({
  positions, adsKey, height, loading, cursorTimestamp, timelineTimestamps,
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const { t } = useTranslation();
  const unit = useMemo(() => formatUnit(adsKey), [adsKey]);
  const label = useMemo(() => t(adsKeyToI18nKey(adsKey), { defaultValue: adsKey }), [adsKey, t]);

  const portalPrimary = useMemo(() => (theme && theme.color && theme.color.primary), []);
  const chartBackground = useMemo(
    () => (theme && theme.color && (theme.color.chartBackground)),
    [],
  );
  const cursorColor = useMemo(
    () => (theme && theme.color && theme.color.primary),
    [],
  );

  const {
    labels, values, timestamps, hasAnyValue,
  } = useMemo(() => buildChartData(positions, adsKey, timelineTimestamps), [positions, adsKey, timelineTimestamps]);

  const cursorPlugin = useMemo(() => ({
    id: 'cursorLine',
    afterDatasetsDraw: (chart) => {
      let idx = chart._cursorIndex;
      if (idx == null || !chart.scales || !chart.scales.x) return;
      const dataLen = (chart.data && chart.data.labels) ? chart.data.labels.length : 0;
      if (!dataLen) return;
      if (idx < 0) idx = 0; else if (idx >= dataLen) idx = dataLen - 1;
      const xScale = chart.scales.x;
      const x = typeof xScale.getPixelForValue === 'function'
        ? xScale.getPixelForValue(idx)
        : xScale.getPixelForTick(idx);
      const {
        top, bottom, right, left,
      } = chart.chartArea || {};
      if (x == null || top == null) return;
      const { ctx } = chart;
      ctx.save();
      ctx.strokeStyle = cursorColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      const clampedX = Math.max(left, Math.min(right, x));
      ctx.moveTo(clampedX, top);
      ctx.lineTo(clampedX, bottom);
      ctx.stroke();
      ctx.restore();
    },
  }), [cursorColor]);

  useEffect(() => {
    if (!canvasRef.current) {
      return () => {
      };
    }

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!hasAnyValue) {
      return () => {
      };
    }

    const ctx = canvasRef.current.getContext('2d');
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label,
            data: values,
            borderColor: portalPrimary,
            backgroundColor: chartBackground,
            pointRadius: 0,
            borderWidth: 1,
            tension: 0.2,
            fill: true,
            spanGaps: true,
          },
        ],
      },
      options: chartOptions(label, unit, timestamps, adsKey),
      plugins: [cursorPlugin],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [labels, values, unit, label, cursorPlugin, hasAnyValue, portalPrimary, chartBackground, timestamps, adsKey]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (!cursorTimestamp) {
      chart._cursorIndex = null;
      chart.update('none');
      return;
    }
    let idx = null;
    if (timestamps && timestamps.length) {
      let lo = 0;
      let hi = timestamps.length - 1;
      let pos = timestamps.length - 1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (timestamps[mid] >= cursorTimestamp) {
          pos = mid;
          hi = mid - 1;
        } else {
          lo = mid + 1;
        }
      }
      const candA = pos;
      const candB = Math.max(0, pos - 1);
      const distA = Math.abs((timestamps[candA] || Infinity) - cursorTimestamp);
      const distB = Math.abs((timestamps[candB] || Infinity) - cursorTimestamp);
      idx = distB < distA ? candB : candA;
    }
    chart._cursorIndex = Number.isFinite(idx) ? idx : null;
    chart.update('none');
  }, [cursorTimestamp, timestamps]);

  if (loading) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      >
        <Spin />
      </div>
    );
  }

  if (!hasAnyValue) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

AdsTimeseriesChart.propTypes = {
  positions: PropTypes.arrayOf(PropTypes.shape({
    positionTimestamp: PropTypes.number,
    serverTimestamp: PropTypes.number,
    ads: PropTypes.objectOf(PropTypes.number),
  })),
  adsKey: PropTypes.string.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  cursorTimestamp: PropTypes.number,
  timelineTimestamps: PropTypes.arrayOf(PropTypes.number),
};

AdsTimeseriesChart.defaultProps = {
  positions: [],
  height: 160,
  loading: false,
  cursorTimestamp: null,
  timelineTimestamps: null,
};
